import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { ChatHeader } from '../../components/Pleri/components/ChatBox/ChatHeader';
import { useLayoutStore } from '../../store/layoutStore';
import { usePleriRenderOptional } from '../../components/Pleri/context/usePleriRenderOptional';
import { useChatStore } from '../../components/Pleri/store/chatStore';
import { MiniSidebar } from '../../components/Pleri/components/ChatBox/MiniSidebar';

const getPanelVariants = (targetWidth: number): Variants => ({
  hidden: {
    width: 0,
    opacity: 0,
    x: 100,
    scale: 0.9,
  },
  visible: {
    width: targetWidth,
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 35,
      mass: 0.8,
      width: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
      },
    },
  },
  exit: {
    width: 0,
    opacity: 0,
    x: 100,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
      width: {
        duration: 0.35,
        ease: [0.4, 0, 0.2, 1] as const,
      },
    },
  },
});

const headerVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.15,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

const contentVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    y: 5,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

export const MiniLayout = () => {
  const [isSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();
  const pleriRenderContext = usePleriRenderOptional();

  const handleClose = () => {
    setIsVisible(false);
    // Delay navigation to allow exit animation
    setTimeout(() => {
      if (pleriRenderContext) {
        pleriRenderContext.close();
      }
      navigate({ to: '/trigger' });
    }, 300);
  };

  const handleTriggerClick = () => {
    // Trigger is already open (we're in mini view), so clicking it closes
    handleClose();
  };

  const { setActiveConversation, activeConversationId, createConversation } =
    useChatStore();

  // Create a conversation when the component mounts if there's no active conversation
  useEffect(() => {
    if (!activeConversationId) {
      const newId = createConversation();
      setActiveConversation({ conversationId: newId });
    }
  }, [activeConversationId, createConversation, setActiveConversation]);

  // Note: We use setMiniMode directly here because when toggling from mini to fullscreen,
  // we don't want to close the focused view. Only use minimizeToMiniMode when minimizing FROM fullscreen.

  return (
    <div
      className="flex h-screen w-full"
      style={{ position: 'relative', zIndex: '2147483642' }}
    >
      <MiniSidebar onClick={handleTriggerClick} />
      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            key="mini-panel"
            variants={getPanelVariants(isSidebarOpen ? 612 : 420)}
            initial="hidden"
            animate="visible"
            exit="exit"
            id="pleri-mini-layout"
            className="flex h-full flex-col overflow-hidden border-l border-gray-200 bg-white shadow-2xl ring-1 ring-gray-200/50 dark:border-gray-700 dark:bg-gray-900 dark:ring-gray-700/50"
          >
            <motion.div variants={headerVariants}>
              <ChatHeader
                isFullScreen={false}
                onFullScreenToggle={() => {
                  // When toggling from mini to fullscreen, we don't need to close focused view
                  // So we use setMiniMode directly instead of minimizeToMiniMode
                  useLayoutStore.getState().setMiniMode(false);
                }}
                onClose={handleClose}
                className="px-4 py-3"
              />
            </motion.div>
            <motion.div
              variants={contentVariants}
              className="relative h-full flex-1 overflow-y-auto"
            >
              <Outlet />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
